import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import RiderLayout from "@/components/RiderLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { 
  Package, CheckCircle2, IndianRupee, Clock, RefreshCw,
  Calendar, Truck, ChevronRight, ArrowLeft, Search, Copy,
  Navigation, Route, Filter, AlertCircle
} from "lucide-react";

const RiderHistory = ({ user, onLogout }) => {
  const [loading, setLoading] = useState(true);
  const [trips, setTrips] = useState([]);
  const [stats, setStats] = useState({
    totalTrips: 0,
    totalTasks: 0,
    totalCashCollected: 0,
  });
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [tripShipments, setTripShipments] = useState([]);
  const [loadingShipments, setLoadingShipments] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [hubLocation, setHubLocation] = useState(null);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/rider/trips/history`);
      const tripsData = res.data.data || [];
      setTrips(tripsData);
      
      // Calculate stats
      setStats({
        totalTrips: tripsData.length,
        totalTasks: tripsData.reduce((sum, t) => sum + (t.taskCount || 0), 0),
        totalCashCollected: tripsData.reduce((sum, t) => sum + (t.cashCollected || 0), 0),
      });
      
      if (res.data.message) {
        toast.info(res.data.message);
      }
    } catch (error) {
      console.error("Error fetching history:", error);
      toast.error("Failed to load history");
    } finally {
      setLoading(false);
    }
  };

  const fetchTripShipments = async (tripId) => {
    setLoadingShipments(true);
    try {
      const [shipmentsRes, hubRes] = await Promise.all([
        axios.get(`${API}/pharmeasy-orders/trips/${tripId}/shipments`),
        axios.get(`${API}/pharmeasy-orders/trips/${tripId}/hub-location`).catch(() => ({ data: null }))
      ]);
      
      // Sort by sequenceByEdg (optimized route order)
      const shipments = (shipmentsRes.data.data || []).sort((a, b) => {
        const seqA = a.sequenceByEdg ?? 999;
        const seqB = b.sequenceByEdg ?? 999;
        return seqA - seqB;
      });
      setTripShipments(shipments);
      setHubLocation(hubRes.data);
    } catch (error) {
      console.error("Failed to fetch shipments:", error);
      setTripShipments([]);
      setHubLocation(null);
      toast.error("Failed to load deliveries");
    } finally {
      setLoadingShipments(false);
    }
  };

  const viewDeliveries = (trip) => {
    setSelectedTrip(trip);
    fetchTripShipments(trip.tripId);
  };

  const goBack = () => {
    setSelectedTrip(null);
    setTripShipments([]);
    setHubLocation(null);
    setSearchQuery("");
    setStatusFilter("all");
  };

  const copyToClipboard = (text, label) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied!`);
  };

  const getMapLink = (shipment) => {
    // For RETURN shipments, ONLY use srcAddress (customer pickup location)
    if (shipment?.type === "RETURN") {
      const calcSrc = shipment?.calculatedSrcAddressCoordinate;
      if (calcSrc?.success && calcSrc?.coordinates) {
        return `https://www.google.com/maps/search/?api=1&query=${calcSrc.coordinates.lat},${calcSrc.coordinates.lng}`;
      }
      const srcAddr = shipment?.srcAddress;
      if (srcAddr?.latitude && srcAddr?.longitude) {
        const lat = parseFloat(srcAddr.latitude);
        const lng = parseFloat(srcAddr.longitude);
        if (lat !== 0 && lng !== 0 && !isNaN(lat) && !isNaN(lng)) {
          return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
        }
      }
    } else {
      const calcCoords = shipment?.calculatedDestAddressCoordinate;
      if (calcCoords?.success && calcCoords?.coordinates) {
        return `https://www.google.com/maps/search/?api=1&query=${calcCoords.coordinates.lat},${calcCoords.coordinates.lng}`;
      }
      const destAddr = shipment?.destAddress;
      if (destAddr?.latitude && destAddr?.longitude) {
        const lat = parseFloat(destAddr.latitude);
        const lng = parseFloat(destAddr.longitude);
        if (lat !== 0 && lng !== 0 && !isNaN(lat) && !isNaN(lng)) {
          return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
        }
      }
    }
    return null;
  };

  const formatAddress = (shipment) => {
    // For RETURN shipments, ONLY use srcAddress
    if (shipment?.type === "RETURN") {
      const src = shipment?.srcAddress;
      if (!src) return "—";
      const parts = [src.address1, src.address2, src.city, src.state, src.pincode].filter(Boolean);
      return parts.join(", ") || "—";
    }
    const dest = shipment?.destAddress;
    if (!dest) return "—";
    const parts = [dest.address1, dest.address2, dest.city, dest.state, dest.pincode].filter(Boolean);
    return parts.join(", ") || "—";
  };

  // Get coordinates for a shipment - RETURN uses only srcAddress
  const getCoordinates = (shipment) => {
    if (shipment?.type === "RETURN") {
      // RETURN: ONLY use srcAddress (customer pickup location)
      const calcSrc = shipment?.calculatedSrcAddressCoordinate;
      if (calcSrc?.success && calcSrc?.coordinates) {
        return { lat: calcSrc.coordinates.lat, lng: calcSrc.coordinates.lng };
      }
      const srcAddr = shipment?.srcAddress;
      if (srcAddr?.latitude && srcAddr?.longitude) {
        const lat = parseFloat(srcAddr.latitude);
        const lng = parseFloat(srcAddr.longitude);
        if (lat !== 0 && lng !== 0 && !isNaN(lat) && !isNaN(lng)) {
          return { lat, lng };
        }
      }
    } else {
      const calcCoords = shipment?.calculatedDestAddressCoordinate;
      if (calcCoords?.success && calcCoords?.coordinates) {
        return { lat: calcCoords.coordinates.lat, lng: calcCoords.coordinates.lng };
      }
      const destAddr = shipment?.destAddress;
      if (destAddr?.latitude && destAddr?.longitude) {
        const lat = parseFloat(destAddr.latitude);
        const lng = parseFloat(destAddr.longitude);
        if (lat !== 0 && lng !== 0 && !isNaN(lat) && !isNaN(lng)) {
          return { lat, lng };
        }
      }
    }
    return null;
  };

  const getFullRouteUrl = () => {
    const waypoints = tripShipments
      .map(s => getCoordinates(s))
      .filter(Boolean)
      .map(c => `${c.lat},${c.lng}`);

    if (waypoints.length === 0) return null;
    
    // Start from hub location if available
    const originCoords = hubLocation?.coordinates;
    const originStr = originCoords ? `${originCoords.lat},${originCoords.lng}` : waypoints[0];
    
    const destination = waypoints[waypoints.length - 1];
    const waypointsStr = waypoints.slice(0, -1).join('/');
    
    return `https://www.google.com/maps/dir/${originStr}/${waypointsStr}/${destination}`;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      return new Date(dateStr).toLocaleString('en-IN', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "COMPLETED":
        return <Badge className="bg-green-100 text-green-700">Completed</Badge>;
      case "RECON_FINISHED":
        return <Badge className="bg-purple-100 text-purple-700">Reconciled</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getShipmentStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case "DELIVERED":
        return "bg-green-100 text-green-700 border-green-200";
      case "OUT_FOR_DELIVERY":
        return "bg-blue-100 text-blue-700 border-blue-200";
      case "PENDING":
        return "bg-amber-100 text-amber-700 border-amber-200";
      case "CANCELLED":
      case "REJECTED":
      case "RTO":
        return "bg-red-100 text-red-700 border-red-200";
      case "ATTEMPTED":
        return "bg-orange-100 text-orange-700 border-orange-200";
      default:
        return "bg-slate-100 text-slate-600 border-slate-200";
    }
  };

  // Get unique statuses from shipments
  // Get unique statuses from shipments (prefer bolt_status over status)
  const uniqueStatuses = [...new Set(tripShipments.map(s => s.bolt_status || s.status).filter(Boolean))];

  // Filter shipments based on search and status
  const filteredShipments = tripShipments.filter(s => {
    // Status filter
    // Status filter - prefer bolt_status over status
    const shipmentStatus = s.bolt_status || s.status;
    if (statusFilter !== "all" && shipmentStatus?.toUpperCase() !== statusFilter.toUpperCase()) {
      return false;
    }
    // Search filter
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      s.shipmentId?.toString().includes(q) ||
      s.packageId?.toLowerCase().includes(q) ||
      s.referenceId?.toLowerCase().includes(q) ||
      s.destAddress?.address1?.toLowerCase().includes(q) ||
      s.destAddress?.pincode?.includes(q)
    );
  });

  // Show shipment list view
  if (selectedTrip) {
    const fullRouteUrl = getFullRouteUrl();

    return (
      <RiderLayout user={user} onLogout={onLogout}>
        <div className="space-y-4" data-testid="rider-history-deliveries">
          {/* Header */}
          <div className="flex items-center gap-3 mb-2">
            <Button variant="ghost" size="sm" onClick={goBack} className="p-2">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-lg font-bold text-slate-800">Trip #{selectedTrip.tripId}</h1>
              <p className="text-xs text-slate-500">{tripShipments.length} deliveries</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchTripShipments(selectedTrip.tripId)}
              disabled={loadingShipments}
            >
              <RefreshCw className={`w-4 h-4 ${loadingShipments ? 'animate-spin' : ''}`} />
            </Button>
          </div>

          {/* Search Bar */}
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              type="text"
              placeholder="Search by ID, Package, Address..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-white"
            />
          </div>

          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-400" />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full bg-white">
                <SelectValue placeholder="Filter by Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {uniqueStatuses.map((status) => (
                  <SelectItem key={status} value={status}>
                    {status?.replace(/_/g, " ")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Open Full Route Button */}
          {fullRouteUrl && (
            <a
              href={fullRouteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full py-3 bg-emerald-500 text-white rounded-lg font-medium hover:bg-emerald-600 transition-colors"
            >
              <Route className="w-5 h-5" />
              Open Full Route in Maps
            </a>
          )}

          {/* Shipments List */}
          {loadingShipments ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : filteredShipments.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 font-medium">
                  {searchQuery ? "No matching deliveries" : "No deliveries found"}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {filteredShipments.map((shipment, index) => {
                const mapLink = getMapLink(shipment);
                const fullAddress = formatAddress(shipment);
                
                return (
                  <Card key={shipment._id || index} className="overflow-hidden">
                    <CardContent className="p-0">
                      {/* Sequence & Priority Header */}
                      <div className="flex items-center justify-between px-4 py-2 bg-slate-50 border-b">
                        <div className="flex items-center gap-3">
                          <div className="flex flex-col items-center">
                            <Badge className="bg-emerald-100 text-emerald-700 font-bold text-sm">
                              #{shipment.sequenceByEdg || index + 1}
                            </Badge>
                            <span className="text-[10px] text-slate-400 mt-0.5">Edg Seq</span>
                          </div>
                          <div className="flex flex-col items-center">
                            <Badge variant="outline" className="font-medium text-sm">
                              P{shipment.priority ?? "—"}
                            </Badge>
                            <span className="text-[10px] text-slate-400 mt-0.5">Priority</span>
                          </div>
                        </div>
                        {mapLink && (
                          <a
                            href={mapLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-blue-600 text-sm font-medium"
                          >
                            <Navigation className="w-4 h-4" />
                            Navigate
                          </a>
                        )}
                      </div>

                      {/* Status Banner */}
                      <div className={`px-4 py-2 ${getShipmentStatusColor(shipment.bolt_status || shipment.status)} border-b flex items-center gap-2`}>
                        <AlertCircle className="w-4 h-4" />
                        <span className="text-sm font-semibold">
                          {(shipment.bolt_status || shipment.status)?.replace(/_/g, " ") || "PENDING"}
                        </span>
                      </div>

                      {/* Shipment Details */}
                      <div className="p-4 space-y-3">
                        {/* Package ID */}
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-slate-500">Package ID</span>
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm font-medium">{shipment.packageId || "—"}</span>
                            {shipment.packageId && (
                              <button 
                                onClick={() => copyToClipboard(shipment.packageId, 'Package ID')}
                                className="text-slate-400 hover:text-blue-600 p-1"
                              >
                                <Copy className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Reference ID */}
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-slate-500">Reference ID</span>
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-xs text-slate-600 truncate max-w-[180px]">
                              {shipment.referenceId || "—"}
                            </span>
                            {shipment.referenceId && (
                              <button 
                                onClick={() => copyToClipboard(shipment.referenceId, 'Reference ID')}
                                className="text-slate-400 hover:text-blue-600 p-1 flex-shrink-0"
                              >
                                <Copy className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Shipment ID */}
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-slate-500">Shipment ID</span>
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm">{shipment.shipmentId || "—"}</span>
                            {shipment.shipmentId && (
                              <button 
                                onClick={() => copyToClipboard(shipment.shipmentId?.toString(), 'Shipment ID')}
                                className="text-slate-400 hover:text-blue-600 p-1"
                              >
                                <Copy className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Address */}
                        <div className="pt-2 border-t">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1">
                              <p className="text-xs text-slate-500 mb-1">Delivery Address</p>
                              <p className="text-sm text-slate-700 leading-relaxed">{fullAddress}</p>
                            </div>
                            {fullAddress !== "—" && (
                              <button 
                                onClick={() => copyToClipboard(fullAddress, 'Address')}
                                className="text-slate-400 hover:text-blue-600 p-1 flex-shrink-0 mt-4"
                              >
                                <Copy className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </RiderLayout>
    );
  }

  // Show history list view
  return (
    <RiderLayout user={user} onLogout={onLogout}>
      <div className="space-y-4" data-testid="rider-history-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-xl font-bold text-slate-800">Trip History</h1>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchHistory}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-2">
          <Card className="bg-blue-50">
            <CardContent className="p-3 text-center">
              <p className="text-xl font-bold text-blue-800">{stats.totalTrips}</p>
              <p className="text-xs text-blue-600">Trips</p>
            </CardContent>
          </Card>
          <Card className="bg-green-50">
            <CardContent className="p-3 text-center">
              <p className="text-xl font-bold text-green-800">{stats.totalTasks}</p>
              <p className="text-xs text-green-600">Deliveries</p>
            </CardContent>
          </Card>
          <Card className="bg-amber-50">
            <CardContent className="p-3 text-center">
              <p className="text-lg font-bold text-amber-800">{formatCurrency(stats.totalCashCollected)}</p>
              <p className="text-xs text-amber-600">Collected</p>
            </CardContent>
          </Card>
        </div>

        {/* Trips List */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Completed Trips
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : trips.length === 0 ? (
              <div className="text-center py-8">
                <Truck className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No completed trips yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {trips.map((trip, index) => (
                  <div
                    key={trip._id || trip.tripId}
                    className="p-3 bg-slate-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-slate-800">
                            Trip #{trip.tripId}
                          </p>
                          {getStatusBadge(trip.status)}
                        </div>
                        <p className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                          <Package className="w-3 h-3" />
                          {trip.taskCount || 0} deliveries
                          <span className="text-slate-300">|</span>
                          <Clock className="w-3 h-3" />
                          {formatDate(trip.updatedOn)}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        {trip.cashCollected > 0 ? (
                          <p className="text-sm font-medium text-green-600">
                            {formatCurrency(trip.cashCollected)}
                          </p>
                        ) : trip.cashToBeCollected > 0 ? (
                          <p className="text-sm font-medium text-amber-600">
                            {formatCurrency(trip.cashToBeCollected)}
                          </p>
                        ) : (
                          <p className="text-sm text-slate-400">No COD</p>
                        )}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full mt-3"
                      onClick={() => viewDeliveries(trip)}
                    >
                      View Deliveries
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </RiderLayout>
  );
};

export default RiderHistory;
